/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import LiquidFireService from 'cio/services/liquid-fire';
import { scheduleOnce } from '@ember/runloop';
import { EmberRunQueues } from '@ember/runloop/-private/types';
import isDestroyed from 'cio/utils/is-destroyed';
import { task } from 'ember-concurrency';
import { timeout } from 'ember-concurrency';
import { action } from '@ember/object';
import { duration } from 'cio/transitions';

interface LiquidFireDelayedRenderArgs {
  runLoopQueue?: EmberRunQueues;
  showPlaceholder?: boolean;
  placeholderClass?: string;
  showSpinner?: boolean;
  forceRender?: boolean;
}

export default class LiquidFireDelayedRender extends Component<LiquidFireDelayedRenderArgs> {
  @service liquidFire!: LiquidFireService;

  @tracked isAnimating = true;
  @tracked isInitialRender = false;

  get runLoopQueue() {
    return this.args.runLoopQueue ?? 'afterRender';
  }

  constructor(owner: unknown, args: LiquidFireDelayedRenderArgs) {
    super(owner, args);

    this.liquidFire.on('transitionStart', this, this.onTransitionStart);
    this.liquidFire.on('transitionEnd', this, this.onTransitionEnd);

    // If the liquidFire service doesn't have any transitions registered,
    // it means we're looking at a page refresh or direct visit to the page with
    // this component. In this case, we need to make sure the component still
    // renders.
    if (!this.liquidFire.hasTransitionedAtLeastOnce) {
      this.isAnimating = false;
      return;
    }
  }

  willDestroy() {
    super.willDestroy();

    this.liquidFire.off('transitionStart', this, this.onTransitionStart);
    this.liquidFire.off('transitionEnd', this, this.onTransitionEnd);

    this.isAnimating = true;
  }

  @action
  onTransitionStart() {
    if (this.liquidFire.hasTransitionedAtLeastOnce) {
      this.isAnimating = true;
    }
  }

  @action
  onTransitionEnd() {
    this.waitForAnimationFinishTask.perform();
  }

  waitForAnimationFinishTask = task({ restartable: true }, async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (!this.liquidFire.isAnimating) {
        scheduleOnce(this.runLoopQueue, this, () => {
          if (!isDestroyed(this)) {
            this.isAnimating = false;
          }
        });
        break;
      }

      await timeout(duration());
    }
  });
}
